import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Card, CardTitle } from '@shared/ui/Card';
import { FilesMode } from './components/FilesMode';
import { LibraryMode } from './components/LibraryMode';
import { Head, ModeBar, ModeButton } from './styles';
import type { Mode } from './types';

/**
 * Запуск ведомости материалов по РД (модуль 1.2). Основной путь — документы
 * библиотеки РД объекта, которые загрузил и разобрал ПТО: ведомость строится за
 * секунды из готового разбора. Второй — свои файлы (то, чего в библиотеке нет).
 * URL `?project=<id>&revisions=<id>,<id>` подставляет объект и документы (ссылка
 * из заявки в снабжение).
 */
export function MtoVedomostStart() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can_write } = useAgentPermission('mto');
  const { activeProjectId } = useActiveProjectContext();

  const [mode, setMode] = useState<Mode>('library');

  const initialProjectId = params.get('project') ?? activeProjectId ?? '';
  const initialRevisions = (params.get('revisions') ?? '').split(',').filter(Boolean);

  const handleRunCreated = (runId: string) => {
    navigate(`/agents/mto/runs/${runId}`);
  };

  return (
    <Card>
      <Head>
        <CardTitle>Новая ведомость материалов</CardTitle>
      </Head>
      <ModeBar role="group" aria-label="Источник документов РД">
        <ModeButton
          type="button"
          $active={mode === 'library'}
          aria-pressed={mode === 'library'}
          onClick={() => setMode('library')}
        >
          Из библиотеки РД объекта
        </ModeButton>
        <ModeButton
          type="button"
          $active={mode === 'files'}
          aria-pressed={mode === 'files'}
          onClick={() => setMode('files')}
        >
          Свои файлы
        </ModeButton>
      </ModeBar>

      {mode === 'library' ? (
        <LibraryMode
          initialProjectId={initialProjectId}
          initialRevisions={initialRevisions}
          canWrite={can_write}
          onRunCreated={handleRunCreated}
        />
      ) : (
        <FilesMode canWrite={can_write} onRunCreated={handleRunCreated} />
      )}
    </Card>
  );
}

export default MtoVedomostStart;
